import * as React from "react"
import {useState, useEffect, useContext, ReactNode, ReactElement, useLayoutEffect, useRef} from "react";
import { CarouselContentItem } from "../../Model/Sales/CarouselContentItem";
import { ApplicationContext } from "../../ApplicationContext";
import {
    simOnlyOldDataAllowance, simOnlyOldMonthlyCost,
    SimOnlyPlan,
    simOnlyPlanDataLimit, simOnlyPlanDataRoaming,
    simOnlyPlanMonthlyCost,
} from "../../Model/Sales/SimOnlyPlan";
import {renderDynamicContent} from "../../Utilities/CmsUtilities";
import {addPlanCodeQueryParam, addPromoQueryParam, SALES_PORTAL_ROUTES} from "../../Routes/SalesPortalRoutes";
import ReactDOMServer from "react-dom/server";
import parse from "html-react-parser";
import {CarouselContentType} from "../../Model/Sales/CarouselContentType";
import {fetchCarouselContent} from "../../Api/Sales/CarouselApi";
import {isRequestError} from "../../Model/RequestError";

interface PlanCarouselProps {
    availableSimOnlyPlans: SimOnlyPlan[];
    carouselContentType: CarouselContentType;
    initialPlanCode?: string | null;
    initialPromoCode?: string | null;
}

/**
 * PlanCarousel component
 * Renders a carousel of SIM-only plans with dynamic CMS content.
 * Handles swipe gestures and desktop navigation arrows.
 * Responsive design for mobile and desktop.
 */
const PlanCarousel: React.FC<PlanCarouselProps> = (props) => {
    const applicationContext = useContext(ApplicationContext);

    const [carouselContent, setCarouselContent] = React.useState<CarouselContentItem[]>([])
    /** Index of the currently active card in the carousel */
    const [activeIndex, setActiveIndex] = useState(0);
    /** States for the swipe functionality */
    const [startX, setStartX] = useState(0);
    const [endX, setEndX] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);

    const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
    const carouselContainerRef = useRef<HTMLDivElement>(null);

    /**
     * Fetches CMS content and filters it to match available plans from RMW.
     * Sorts the plans by CMS `orderIndex` and stores them in state.
     */
    const setupCarouselContent = async () => {
        fetchCarouselContent(
            applicationContext.salesPortalApiDomain,
            applicationContext.reseller,
            props.carouselContentType
        ).then((carouselContent) => {
            if (isRequestError(carouselContent)) {
                console.log("Error fetching carousel content:", carouselContent);
            } else {
                carouselContent.sort((a, b) => a.orderIndex - b.orderIndex);

                /**
                 * Filters out CMS content that doesn't match any plan in `availableSimOnlyPlans`.
                 * This ensures the carousel only includes active plans and valid promo codes.
                 */
                const filteredContent = carouselContent.filter(item => {
                    return props.availableSimOnlyPlans.some(plan =>
                        plan.packageCode === item.planCode && plan.promoCode === item.promoCode
                    )
                });
                setCarouselContent(filteredContent)

                if (props.initialPlanCode) {
                    const initialIndex = filteredContent.findIndex(item => {
                        if (props.initialPromoCode) {
                            return item.planCode === props.initialPlanCode && item.promoCode === props.initialPromoCode;
                        } else {
                            return item.planCode === props.initialPlanCode && item.promoCode === null;
                        }
                    }
                    );

                    if (initialIndex !== -1) {
                        setActiveIndex(initialIndex);
                    }
                }
            }
        });
    };

    /** Carousel navigation handlers */
    const prevSlide = () => {
        setActiveIndex((prev) => (prev === 0 ? carouselContent.length - 1 : prev - 1));
    };
    const nextSlide = () => {
        setActiveIndex((prev) => (prev === carouselContent.length - 1 ? 0 : prev + 1));
    };

    /**
     * Determines the CSS class for a given card based on its position relative to the active slide.
     * @param index - Index of the card
     * @returns A string representing the position class ("left", "center", "right", or "hidden")
     */
    const getPositionClass = (index: number) => {
        if (index === activeIndex) return "center";
        if (index === (activeIndex - 1 + carouselContent.length) % carouselContent.length) return "left";
        if (index === (activeIndex + 1) % carouselContent.length) return "right";
        return "hidden";
    };

    /** Carousel swipe gesture handlers */
    const handleTouchStart = (e: React.TouchEvent) => {
        setStartX(e.touches[0].clientX);
        setIsSwiping(false);
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        const moveX = e.touches[0].clientX - startX;
        if (Math.abs(moveX) > 10) { // small threshold to detect swipe
            setIsSwiping(true);
            setEndX(e.touches[0].clientX);
        }
    };
    const handleTouchEnd = () => {
        if (isSwiping) {
            if (startX - endX > 50) nextSlide();
            else if (endX - startX > 50) prevSlide();
        }
        setStartX(0);
        setEndX(0);
        setIsSwiping(false);
    };

    /** Carousel keyboard navigation for enhanced accessibility*/
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case "ArrowLeft":
                prevSlide();
                e.preventDefault();
                break;
            case "ArrowRight":
                nextSlide();
                e.preventDefault();
                break;
            default:
                break;
        }
    };

    /**
     * Recursively adds a CSS class to all elements with the "sim-container" class.
     * Used to apply correct positioning and click handlers for carousel cards functionality.
     * @param element - The React node to modify
     * @param extraClass - The CSS class to append
     * @param onClickHandler - Click handler
     * @returns A new ReactNode with the extra class and handler applied
     */
    function addClassToSimContainer(
        element: ReactNode,
        extraClass: string,
        onClickHandler: (e: React.MouseEvent) => void
    ): ReactNode {
        if (!React.isValidElement(element)) return element;
        const el = element as ReactElement & { props: { className?: string; onClick?: (e: React.MouseEvent) => void } };
        let newProps: Partial<typeof el.props> = {};

        if (el.props.className && el.props.className.includes("sim-container")) {
            newProps.className = `${el.props.className} ${extraClass}`;
            if (onClickHandler) {
                newProps.onClick = (e: React.MouseEvent) => {
                    if (el.props.onClick) el.props.onClick(e);
                    onClickHandler(e);
                };
            }
        }
        return React.cloneElement(
            el,
            newProps,
            React.Children.map(el.props.children, child =>
                addClassToSimContainer(child, extraClass, onClickHandler)
            )
        );
    }

    /**
     * Scopes CSS selectors to a specific class to prevent style collisions.
     * Scoping is needed because CMS content for most plans have the same named element classes with custom styles.
     * @param css - The raw CSS string
     * @param scope - The class to scope the CSS to (e.g., ".plan-123")
     * @returns Scoped CSS string
     */
    function scopeCss(css: string, scope: string): string {
        if (!css || !scope) return css;
        return css.replace(/(^|\})\s*([^{]+)/g, (match: string, brace: string, selectors: string) => {
            const trimmedSelectors = selectors.trim();
            if (trimmedSelectors.startsWith('@') || trimmedSelectors.includes('keyframes') || trimmedSelectors.startsWith(':root')) {
                return `${brace} ${selectors}`;
            }
            const scopedSelectors = trimmedSelectors
                .split(',')
                .map(sel => sel.trim().startsWith(scope) ? sel.trim() : `${scope} ${sel.trim()}`)
                .join(', ');
            return `${brace} ${scopedSelectors}`;
        });
    }

    function getViewPlanLink(plan: SimOnlyPlan): string {
        const isStandard = props.carouselContentType === CarouselContentType.STANDARD;
        const baseRoute = isStandard
            ? SALES_PORTAL_ROUTES.SimOnly.Deals(applicationContext.urlContext)
            : SALES_PORTAL_ROUTES.SimOnly.PurchaseSignUp(applicationContext.urlContext, plan.packageCode);

        let link = addPlanCodeQueryParam(baseRoute, plan.packageCode);

        if (plan.promoCode) {
            link = addPromoQueryParam(link, plan.promoCode);
        }

        return link;
    }

    function getBuyPlanLink(plan: SimOnlyPlan): string {
        const baseRoute =  SALES_PORTAL_ROUTES.SimOnly.PurchaseSignUp(applicationContext.urlContext, plan.packageCode);

        let link = addPlanCodeQueryParam(baseRoute, plan.packageCode);

        if (plan.promoCode) {
            link = addPromoQueryParam(link, plan.promoCode);
        }

        return link;
    }


    function formatPriceNumber(value: number): string {
        if (value % 1 === 0) {
            return value.toString();
        }
        return value.toFixed(2);
    }

    const updateHeight = () => {
        if (!carouselContainerRef.current) return;
        const centerCard = carouselContainerRef.current.querySelector<HTMLDivElement>('.sim-container.center');
        if (!centerCard) return;
        const scaledHeight = centerCard.getBoundingClientRect().height;
        console.log(scaledHeight)
        setContainerHeight(scaledHeight);
    };

    useEffect(() => {
        setupCarouselContent();
    }, []);

    /**
     * This is used to add margin-top to carousel container when user rotate their phone/tablet screen
     */
    useLayoutEffect(() => {
        const applyOffset = () => {
            const scrollHeight = document.documentElement.scrollHeight;
            const windowHeight = window.innerHeight;
            let offset = scrollHeight * 0.1;
            const isPortrait = window.matchMedia("(orientation: portrait)").matches;

            offset = isPortrait ? Math.min(offset, 0) : Math.min(offset, 50);

            if (carouselContainerRef.current) {
                carouselContainerRef.current.style.marginTop = `${offset}px`;
            }
        };

        applyOffset();
        window.addEventListener("resize", applyOffset);
        return () => window.removeEventListener("resize", applyOffset);
    }, []);

    /**
     * This is only used to resize the height of the carousel based on the sim-container center.
     * This makes sure that the navigation dots are in visible and the carousel height is not too big or small.
     */
    useEffect(() => {
        const updateHeight = () => {
            const centerCard = carouselContainerRef.current?.querySelector<HTMLDivElement>('.sim-container.center');
            if (!centerCard) return;
            setContainerHeight(centerCard.getBoundingClientRect().height);
        };

        const centerCard = carouselContainerRef.current?.querySelector<HTMLDivElement>('.sim-container.center');
        if (!centerCard) return;

        // Trigger height update after first render
        requestAnimationFrame(() => updateHeight());

        // Wait until the sim-container transitions end
        let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
        const handleResize = () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                updateHeight();
            }, 300);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimeout) clearTimeout(resizeTimeout);
        };
    }, [carouselContent]);

    /** Inline CSS for the carousel to create layout, animations and responsiveness */
    const planCarouselCss = `
    /* --------- Carousel --------- */
    .carousel-full-wrapper {
      position: relative;
      width: 100%;          /* full width for arrows */
      display: flex;
      justify-content: center;
      align-items: center;
      touch-action: pan-y;
    }
    .carousel-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 5vh 5vh 0vh 5vh;
    }
    .carousel-container {
      position: relative;
      max-width: 100%;
      display: flex;
    }
    
    /* --------- Sim Container --------- */
    .sim-container {
      left: 50%;
      transition: transform 0.6s ease, opacity 0.6s ease, box-shadow 0.6s ease;
      transform: translateX(-50%);
      opacity: 0;
      z-index: 0;
      position: relative;
      transform-origin: top center;
    }
    
    /* Active (center) card */
    .sim-container.center {
      transform: translateX(-50%) translateY(-10%) scale(1.1);
      opacity: 1;
      z-index: 2;
      box-shadow: 0 8px 20px rgba(0,0,0,0.15);
    }
    .sim-container.left {
      transform: translateX(-150%) scale(0.85);
      opacity: 0.3;
      z-index: 1;
      cursor: pointer;
      transition: box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out;
    }
    .sim-container.right {
      transform: translateX(50%) scale(0.85);
      opacity: 0.3;
      z-index: 1;
      cursor: pointer;
      transition: box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out;
    }
    /* Hidden cards */
    .sim-container.hidden {
      opacity: 0;
      pointer-events: none;
      transform: translateX(-50%) scale(0.5);
    }
    /* Glow effect on hover */
    .sim-container.left:hover,
    .sim-container.right:hover {
        box-shadow: 0 0 20px 5px rgba(0, 172, 205, 0.6); /* soft blue glow */
    }
    
    /* --------- Navigation Buttons --------- */
    .nav-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: white;
      border: 2px solid rgb(0, 172, 205);
      border-radius: 50%;
      width: 50px;
      height: 50px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      font-size: 24px;
      font-weight: bold;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
      z-index: 5;
      user-select: none;
      transition: transform 0.2s ease-in-out;
    }
    .nav-button.left { left: 20px; }
    .nav-button.right { right: 20px; }
    .nav-button:hover { transform: translateY(-50%) scale(1.2); }
    
    /* --------- Dots Indicator --------- */
    .carousel-dots {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
    }
    .carousel-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: #ccc;
      transition: background-color 0.3s ease, transform 0.3s ease;
      cursor: pointer;
    }
    .carousel-dot.active {
      background-color: rgb(0, 172, 205);
      transform: scale(1.3);
    }
    
    /* --------- Responsive --------- */
    @media (max-width: 768px) {
      .carousel-wrapper {
        min-height: auto;
      } 
      .sim-container {
        width: 70%;
        min-height: auto;
        max-height: none;
      }
    
      /* Show half left/right */
      .sim-container.left {
        transform: translateX(-110%) scale(0.6);
      }
      .sim-container.right {
        transform: translateX(10%) scale(0.6);
      }
      .sim-container.center {
        transform: translateX(-50%) translateY(-10%) scale(0.8);
        opacity: 1;
        z-index: 2;
      }
    
      .nav-button { display: none; } /* Hide arrows */
    }
    /* Small phones */
    @media (max-width: 576px) {
    
      /* Show half left/right */
      .sim-container.left {
        transform: translateX(-100%) scale(0.5);
      }
      .sim-container.right {
        transform: translateX(0%) scale(0.5);
      }
      .sim-container.center {
        transform: translateX(-50%) translateY(-5%) scale(0.6);
        opacity: 1;
        z-index: 2;
      }
    }
    /* Tiny Screens */
    @media (max-width: 375px) {
    
      .sim-container.left,
      .sim-container.right {
        display: none !important;
      }
      .sim-container.center {
        transform: translateX(-50%) translateY(-5%) scale(0.5);
        opacity: 1;
        z-index: 2;
      }
      .carousel-dots {
        transform: scale(0.7);
      }
      
      .nav-button {
        display: flex;
      }
      .nav-button.left { left: -30px; }
      .nav-button.right { right: -30px; }
    }
    `

    return (
        <div>
            <style>{planCarouselCss}</style>
            <div className="carousel-full-wrapper">
                {/*Desktop Arrow Left*/}
                <button className="nav-button left" onClick={prevSlide}>
                    &lt;
                </button>
                <div className="carousel-wrapper">
                    <div
                        className="carousel-container"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onKeyDown={handleKeyDown}
                        onMouseDown={(e) => e.preventDefault()}
                        tabIndex={0}
                        role="region"
                        aria-roledescription="carousel"
                        aria-label="Available mobile plans carousel"
                        aria-live="polite"
                        style={{ height: containerHeight ? `${containerHeight}px` : 'auto' }}
                        ref={carouselContainerRef}
                    >

                        {/* Render all cards but only show the active, left, and right ones */}
                        {carouselContent && carouselContent.map( (contentItem, index) =>{
                            const positionClass = getPositionClass(index);
                            const handleClick = () => {
                                if (positionClass === "left") prevSlide();
                                if (positionClass === "right") nextSlide();
                            };
                            const plan = props.availableSimOnlyPlans.find((p) =>
                                p.packageCode === contentItem.planCode && p.promoCode === contentItem.promoCode
                            );
                            if (!plan) return null; // Skip if no matching plan

                            let templateTags;
                            if (plan.promoCode === null){
                                templateTags = [
                                    { key: "DATA_ALLOWANCE", value: simOnlyPlanDataLimit(plan) },
                                    { key: "MONTHLY_PRICE", value: formatPriceNumber(simOnlyPlanMonthlyCost(plan)) },
                                    { key: "ROAMING_DATA", value: simOnlyPlanDataRoaming(plan)},
                                    {
                                        key: "VIEW_PLAN_LINK",
                                        value: getViewPlanLink(plan)
                                    },
                                    {
                                        key: "BUY_PLAN_LINK",
                                        value: getBuyPlanLink(plan)
                                    }
                                ];
                            }else {
                                templateTags = [
                                    {key: "OLD_DATA_ALLOWANCE", value: plan.promoAddonLimit ? simOnlyOldDataAllowance(plan) : null},
                                    {key: "OLD_MONTHLY_PRICE", value: plan.promoDiscount?.value ? simOnlyOldMonthlyCost(plan) : null},
                                    {key: "DATA_ALLOWANCE", value: simOnlyPlanDataLimit(plan)},
                                    {key: "MONTHLY_PRICE", value: formatPriceNumber(simOnlyPlanMonthlyCost(plan))},
                                    { key: "ROAMING_DATA", value: simOnlyPlanDataRoaming(plan)},
                                    {
                                        key: "VIEW_PLAN_LINK",
                                        value: getViewPlanLink(plan)
                                    },
                                    {
                                        key: "BUY_PLAN_LINK",
                                        value: getBuyPlanLink(plan)
                                    }
                                ];
                            }
                            const content = renderDynamicContent(contentItem.htmlContentString, templateTags);
                            const htmlString: string = ReactDOMServer.renderToString(content!);
                            const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
                            const extractedCss = htmlString
                                .match(styleRegex)
                                ?.map(match =>
                                    match.replace(/<\/?style[^>]*>/g, "")
                                )
                                .join("\n") || "";

                            const htmlWithoutStyle = htmlString.replace(styleRegex, "");
                            const scopedCss = scopeCss(extractedCss, `.plan-${plan.packageCode}-${plan.promoCode}`);
                            const reactContent = parse(`${htmlWithoutStyle}`) as ReactElement;

                            return (
                                <div
                                    key={`plan-${plan.packageCode}-${plan.promoCode}`}
                                    className={`plan-${plan.packageCode}-${plan.promoCode}`}
                                >
                                    <style>${scopedCss}</style>
                                    {addClassToSimContainer(reactContent, positionClass, handleClick)}
                                </div>
                            )
                        })}
                    </div>
                </div>
                {/*Desktop Arrow Right*/}
                <button className="nav-button right" onClick={nextSlide}>
                    &gt;
                </button>
            </div>
            {/* Dots navigation */}
            <div className="carousel-dots">
                {carouselContent.map((_, index) => (
                    <div
                        key={index}
                        className={`carousel-dot ${
                            // Offsetting by one to show the next slide as active in the dots, this makes the first
                            // card start from second dot which is more intuitive for users.
                            index === (activeIndex + 1) % carouselContent.length ? "active" : "" 
                        }`}
                        onClick={() =>
                            // Because we are offsetting the visual "active" dot by one, we need to adjust the index
                            // accordingly when setting the active index.
                            setActiveIndex((index - 1 + carouselContent.length) % carouselContent.length)
                        }
                        aria-label={`Go to slide ${index + 1}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setActiveIndex(index)}
                    />
                ))}
            </div>
        </div>
    );
};

export default PlanCarousel;
